import type { MatrixClient } from "matrix-js-sdk";
import { useQuery } from "@tanstack/react-query";
import { useCallback } from "react";

export const useMatrixRoom = (
  matrixClient: MatrixClient | null,
  roomAlias: string | null,
) => {
  // Default to null so that `room` is not `undefined` for `Room` typing reasons
  const { data: roomId = null } = useQuery({
    queryKey: ["roomId", !!matrixClient, roomAlias],
    queryFn: () => {
      // React query does not appreciate `undefined`, so return `null` instead
      if (!matrixClient || !roomAlias) {
        return null;
      }
      return matrixClient
        .getRoomIdForAlias(roomAlias)
        .then((room) => room.room_id);
    },
    // Avoid spamming the server with requests for essentially stale data
    staleTime: Infinity,
  });

  // useCallback required since this function is a dependency in some places.
  const getRoom = useCallback(() => {
    if (!matrixClient || !roomAlias || !roomId) {
      return null;
    }
    return matrixClient.getRoom(roomId);
  }, [matrixClient, roomAlias, roomId]);

  return { roomId, getRoom };
};

export const useMatrixRoomAliases = (
  matrixClient: MatrixClient | null,
  roomIds: string[],
) => {
  const { data: roomAliasMap = {} } = useQuery({
    queryKey: ["roomAliases", !!matrixClient, ...roomIds],
    queryFn: async () => {
      if (!matrixClient) {
        return {};
      }
      const entries = await Promise.all(
        roomIds.map(async (roomId) => {
          const { aliases } = await matrixClient.getLocalAliases(roomId);
          return [roomId, aliases] as const;
        }),
      );
      return Object.fromEntries(entries) as Record<string, string[]>;
    },
    // Avoid spamming the server with requests for essentially stale data
    staleTime: Infinity,
  });

  return roomAliasMap;
};
